import { faBookOpen } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { MDXProvider } from '@mdx-js/react';
import { createFileRoute } from '@tanstack/react-router';
import { lazy, Suspense, useMemo } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { DocsLayout } from '../../-components/DocsLayout';
import { mdxComponents } from '../../-components/MDXComponents';

const foundationModules = import.meta.glob('../../-foundations/*.docs.mdx');

function pathToSlug(path: string): string {
  const filename = path.split('/').pop() ?? '';
  return filename.replace('.docs.mdx', '');
}

const slugToModule: Record<string, () => Promise<unknown>> = {};
for (const [path, loader] of Object.entries(foundationModules)) {
  slugToModule[pathToSlug(path)] = loader;
}

export const Route = createFileRoute('/docs/foundations/$slug/')({
  component: FoundationDocPage,
});

function FoundationDocPage() {
  const { slug } = Route.useParams();

  const DocContent = useMemo(() => {
    const loader = slugToModule[slug];
    if (!loader) return null;
    return lazy(loader as () => Promise<{ default: React.ComponentType }>);
  }, [slug]);

  if (!DocContent) {
    return (
      <DocsLayout>
        <div className='flex flex-col items-center justify-center py-24 gap-3 text-center'>
          <FontAwesomeIcon
            icon={faBookOpen}
            className='size-10 text-muted-foreground/30'
          />
          <p className='text-sm font-medium text-foreground'>
            No foundation doc yet for{' '}
            <code className='text-primary'>{slug}</code>
          </p>
          <p className='text-xs text-muted-foreground max-w-xs'>
            Create{' '}
            <code className='text-muted-foreground'>
              docs/-foundations/{slug}.docs.mdx
            </code>{' '}
            to add this foundation.
          </p>
        </div>
      </DocsLayout>
    );
  }

  return (
    <DocsLayout>
      <MDXProvider components={mdxComponents}>
        <Suspense fallback={<DocSkeleton />}>
          <DocContent />
        </Suspense>
      </MDXProvider>
    </DocsLayout>
  );
}

function DocSkeleton() {
  return (
    <div className='space-y-4'>
      <Skeleton className='h-9 w-48' />
      <Skeleton className='h-4 w-96' />
      <Skeleton className='h-4 w-80' />
      <div className='mt-8 space-y-3'>
        <Skeleton className='h-6 w-32' />
        <Skeleton className='h-32 w-full' />
      </div>
    </div>
  );
}
